import React from 'react';
import PropTypes from 'prop-types';
import './Alert.styles.css'; // We will minimize styles in this file
import { CheckCircle, Info, XCircle, WarningCircle } from '@phosphor-icons/react'; // Changed import here

const Alert = ({
  children,
  type = 'info',
  className = '',
  compact = false,
  showIcon = true,
  icon: iconOverride = null,
  ...props
}) => {
  // An explicit `icon` overrides the type-based default (e.g. a red alert that
  // should still show the info "i" rather than the error "x").
  let icon = iconOverride;

  if (!icon) {
    switch (type) {
      case 'success':
        icon = <CheckCircle size={16} weight="bold" />;
        break;
      case 'error':
        icon = <XCircle size={16} weight="bold" />;
        break;
      case 'warning':
        icon = <WarningCircle size={16} weight="bold" />;
        break;
      case 'info':
      default:
        icon = <Info size={16} weight="bold" />;
        break;
    }
  }

  // When compact, arrange icon and header on the same horizontal row and the rest of the content beneath
  if (compact) {
    const headerChildren = [];
    const otherChildren = [];

    React.Children.toArray(children).forEach(child => {
      if (child?.type === Alert.Header) {
        headerChildren.push(child);
      } else {
        otherChildren.push(child);
      }
    });

    return (
      <div
        className={`alert ${type} compact ${className}`}
        role="alert"
        {...props}
      >
        <div className="alert-top flex items-center gap-2">
          {showIcon ? icon : null}
          {headerChildren.length > 0 ? headerChildren : null}
        </div>
        {otherChildren.length > 0 && (
          <div className="alert-body w-full">
            {otherChildren}
          </div>
        )}
      </div>
    );
  }

  // Default (non-compact) layout
  return (
    <div
      className={`alert ${type} ${className}`}
      role="alert"
      {...props}
    >
      <span>{showIcon ? icon : null}</span>
      <span>{children}</span>
    </div>
  );
};

// Semantic sub-components
Alert.Header = ({ children, className = '', ...props }) => (
  <div className={`alert-header ${className}`} {...props}>
    {children}
  </div>
);

Alert.Text = ({ children, className = '', ...props }) => (
  <p className={`alert-text ${className}`} {...props}>
    {children}
  </p>
);

Alert.Details = ({ children, className = '', ...props }) => (
  <div className={`alert-details ${className}`} {...props}>
    {children}
  </div>
);

Alert.propTypes = {
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  className: PropTypes.string,
  compact: PropTypes.bool,
  showIcon: PropTypes.bool,
  icon: PropTypes.node,
};

Alert.Header.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

Alert.Text.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

Alert.Details.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default Alert; 